/** @graphql agg **/
import { graphql } from 'react-relay';

import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import type { BylineContentType } from '../utils/contentAri.ts';

import type {
	preloadRelatedContentSampleQuery$data,
	preloadRelatedContentSampleQuery$variables,
} from './__generated__/preloadRelatedContentSampleQuery.graphql.ts';
import {
	buildRelatedContentSampleVariables,
	normalizeRelatedContentSampleData,
	type RelatedContentSampleResult,
	type RelatedContentSampleUnavailableReason,
} from './relatedContentSsrState.ts';

type RelatedContentSampleCauseCategory = 'error' | 'graphql' | 'none' | 'non-error';

// The bounded related-content sample: up to `RELATED_CONTENT_SAMPLE_LIMIT` entity ARIs the graph
// relates to this page. It warms the AGG query cache so the client can read the same query
// `cache-only` after SSR hydration, mirroring the inbound sample. It selects each node's ARI only;
// the client's `useRelatedContent` runs its own, larger query and hydrates URLs through ORS.
const RelatedContentSampleQuery = graphql`
	query preloadRelatedContentSampleQuery(
		$cypherQuery: String!
		$queryContext: String!
		$version: GraphStoreCypherQueryV2VersionEnum
		$first: Int!
	) {
		graphStore_cypherQueryV2(
			query: $cypherQuery
			queryContext: $queryContext
			version: $version
			first: $first
		) @optIn(to: "GraphStoreCypherQueryV2") {
			edges {
				node {
					columns {
						key
						value {
							__typename
							... on GraphStoreCypherQueryV2AriNode {
								id
							}
						}
					}
				}
			}
		}
	}
`;

const unavailable = (
	reason: RelatedContentSampleUnavailableReason,
): RelatedContentSampleResult => ({
	status: 'unavailable',
	reason,
});

const reportUnavailable = (
	reason: Exclude<RelatedContentSampleUnavailableReason, 'invalid-input'>,
	cause?: unknown,
) => {
	const causeCategory: RelatedContentSampleCauseCategory =
		cause == null
			? 'none'
			: cause instanceof Error
				? cause.name === 'GraphQLError'
					? 'graphql'
					: 'error'
				: 'non-error';

	ssrPrepareErrorsClient.submitError(
		new Error(`Related content sample unavailable: ${reason}`, { cause }),
		{
			operation: 'preload-related-content-sample',
			reason,
			causeCategory,
		},
	);
};

export const preloadRelatedContentSample = async ({
	cloudId,
	activationId,
	contentId,
	contentType,
}: {
	cloudId: string;
	activationId: string;
	contentId: string;
	contentType?: BylineContentType;
}): Promise<RelatedContentSampleResult> => {
	const variables = buildRelatedContentSampleVariables(
		cloudId,
		activationId,
		contentId,
		contentType,
	);
	if (!variables) {
		return unavailable('invalid-input');
	}

	try {
		// No `no-cache`: the sample warms the AGG query cache so the client can read the same query
		// `cache-only` after SSR hydration.
		const { data, errors, hasErrors } = await queryAgg<
			preloadRelatedContentSampleQuery$data,
			preloadRelatedContentSampleQuery$variables
		>({
			query: RelatedContentSampleQuery,
			variables,
		});

		if (hasErrors || errors?.length) {
			reportUnavailable('query-error', errors?.[0]);
			return unavailable('query-error');
		}

		const result = normalizeRelatedContentSampleData(data);
		if (result.status === 'unavailable') {
			reportUnavailable('malformed-response');
		}
		return result;
	} catch (cause) {
		reportUnavailable('query-error', cause);
		return unavailable('query-error');
	}
};
